import React from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { PERSONALITY_GRADIENTS, PERSONALITY_IMAGE_IDS, PERSONALITY_PERCENTILES } from './TravelPersonalityStyles'
import type { TravelPersonalityType } from '../../types/wrapped'
import Label from 'LuxKit/Label'
import BodyText from 'LuxKit/Typography/BodyText'
import { getImageUrl } from 'lib/image/imageUtils'
import Heading from 'LuxKit/Typography/Heading'
import SparklesAnimation from '../SparklesAnimation'
import SlideBase from './SlideBase'
import Sheet from 'components/utils/Sheet'
import { useWrappedStorySelector } from 'contexts/Wrapped'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`

const Content = styled.div`
  position: relative;
  z-index: ${zIndex.min};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(40)};
`

const PersonalityCard = styled(Sheet)`
  position: relative;
  width: 100%;
  max-width: ${rem(327)};
  aspect-ratio: 327 / 446;
  padding: ${rem(24)} ${rem(20)} ${rem(40)};
  border-radius: ${rem(20)};
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  &.the-sun-chaser {
    background: ${PERSONALITY_GRADIENTS['the-sun-chaser']};
  }
  &.the-wellness-wanderer {
    background: ${PERSONALITY_GRADIENTS['the-wellness-wanderer']};
  }
  &.the-nature-roamer {
    background: ${PERSONALITY_GRADIENTS['the-nature-roamer']};
  }
  &.the-bond-builder {
    background: ${PERSONALITY_GRADIENTS['the-bond-builder']};
  }
  &.the-romance-storyteller {
    background: ${PERSONALITY_GRADIENTS['the-romance-storyteller']};
  }
  &.the-trend-explorer {
    background: ${PERSONALITY_GRADIENTS['the-trend-explorer']};
  }
  &.the-gourmet-hunter {
    background: ${PERSONALITY_GRADIENTS['the-gourmet-hunter']};
  }
  &.the-elite-traveller {
    background: ${PERSONALITY_GRADIENTS['the-elite-traveller']};
  }
  &.the-curious-explorer {
    background: ${PERSONALITY_GRADIENTS['the-curious-explorer']};
  }
`

const AvatarContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  aspect-ratio: 1;
  animation: ${float} 3s ease-in-out infinite;
  /* GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const SparklesOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160%;
  height: 160%;
  pointer-events: none;
  z-index: ${zIndex.min};
`

const PersonalityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
`

const LottieStyle = { width: '100%', height: '100%' }

function getPersonalityName(intl: IntlShape, type: TravelPersonalityType): string {
  switch (type) {
    case 'the-bond-builder':
      return intl.formatMessage({
        id: 'ID9bNu42KO',
        defaultMessage: 'The Bond Builder',
        description: 'Travel personality name for the bond builder type in the year-in-review feature',
      })
    case 'the-curious-explorer':
      return intl.formatMessage({
        id: 'ztko3XNpgv',
        defaultMessage: 'The Curious Explorer',
        description: 'Travel personality name for the curious explorer type',
      })
    case 'the-elite-traveller':
      return intl.formatMessage({
        id: 'YQS3qdemXr',
        defaultMessage: 'The Elite Traveller',
        description: 'Travel personality name for the elite traveller type',
      })
    case 'the-nature-roamer':
      return intl.formatMessage({
        id: 'MpPG43Jz2s',
        defaultMessage: 'The Nature Roamer',
        description: 'Travel personality name for the nature roamer type',
      })
    case 'the-romance-storyteller':
      return intl.formatMessage({
        id: 'nXdupWuZbx',
        defaultMessage: 'The romantic retreat',
        description: 'Travel personality name for the romance storyteller type',
      })
    case 'the-sun-chaser':
      return intl.formatMessage({
        id: 'YHnj2Mcco8',
        defaultMessage: 'The Sun Chaser',
        description: 'Travel personality name for the sun chaser type in the year-in-review feature',
      })
    case 'the-wellness-wanderer':
      return intl.formatMessage({
        id: 'N4P3lBIRSl',
        defaultMessage: 'The Wellness Wanderer',
        description: 'Travel personality name for the wellness wanderer type',
      })
    case 'the-trend-explorer':
      return intl.formatMessage({
        id: 'JaAlMWFYkL',
        defaultMessage: 'The Trend Explorer',
        description: 'Travel personality name for the trend explorer type',
      })
    case 'the-gourmet-hunter':
      return intl.formatMessage({
        id: 'S8EYPiMm3B',
        defaultMessage: 'The Gourmet Hunter',
        description: 'Travel personality name for the gourmet hunter type',
      })
  }
}

function getPersonalityDescription(intl: IntlShape, type: TravelPersonalityType): string {
  switch (type) {
    case 'the-bond-builder':
      return intl.formatMessage({
        id: 'l3QxI49zXW',
        defaultMessage: 'You loved travelling with friends and family, choosing escapes that brought people closer through shared moments and meaningful memories.',
        description: 'Travel personality description for the bond builder type',
      })
    case 'the-curious-explorer':
      return intl.formatMessage({
        id: 'VLG+ZSgJUU',
        defaultMessage: 'You discovered a bit of everything that inspires you, following your curiosity to new destinations.',
        description: 'Travel personality description for the curious explorer type',
      })
    case 'the-elite-traveller':
      return intl.formatMessage({
        id: 'VULZopYep1',
        defaultMessage: 'You were drawn to premium escapes and exclusive experiences, valuing exceptional details and elevated comforts at every step.',
        description: 'Travel personality description for the elite traveller type',
      })
    case 'the-nature-roamer':
      return intl.formatMessage({
        id: 'svgOisuEDh',
        defaultMessage: 'You were drawn to wide open spaces and outdoor adventures, where nature set the pace and every day felt refreshing.',
        description: 'Travel personality description for the nature roamer type',
      })
    case 'the-romance-storyteller':
      return intl.formatMessage({
        id: 'LYLyMavx/v',
        defaultMessage: 'You were inspired by intimate escapes, where beautiful settings created space for connection and special moments.',
        description: 'Travel personality description for the romance storyteller type',
      })
    case 'the-sun-chaser':
      return intl.formatMessage({
        id: 'dxiuk9fDtC',
        defaultMessage: 'You loved sun-soaked escapes, choosing destinations that felt warm, uplifting and perfect for switching off.',
        description: 'Travel personality description for the sun chaser type',
      })
    case 'the-wellness-wanderer':
      return intl.formatMessage({
        id: 'BBYzIx/2GO',
        defaultMessage: 'You sought calm, balance and time to slow down, gravitating towards escapes that helped you relax and reset.',
        description: 'Travel personality description for the wellness wanderer type',
      })
    case 'the-trend-explorer':
      return intl.formatMessage({
        id: 'l4uNsCZFH9',
        defaultMessage: 'You gravitated towards stylish stays and vibrant cities, always curious about what was new, exciting and worth discovering.',
        description: 'Travel personality description for the trend explorer type',
      })
    case 'the-gourmet-hunter':
      return intl.formatMessage({
        id: 'M3cJ+prggP',
        defaultMessage: 'You travelled with your taste buds, seeking destinations where food, drink and memorable dining experiences took centre stage.',
        description: 'Travel personality description for the gourmet hunter type',
      })
  }
}

function PersonalityDisplayName({ type }: { type: TravelPersonalityType }) {
  switch (type) {
    case 'the-bond-builder':
      return <FormattedMessage
        id="nmfAJLMRPW"
        defaultMessage="The Bond{br}Builder"
        description="Travel personality heading with line break for bond builder type"
        values={{ br: <br /> }}
      />
    case 'the-curious-explorer':
      return <FormattedMessage
        id="Gv/QaFIYjT"
        defaultMessage="The Curious{br}Explorer"
        description="Travel personality heading with line break for curious explorer type"
        values={{ br: <br /> }}
      />
    case 'the-elite-traveller':
      return <FormattedMessage
        id="vPET6vsWMU"
        defaultMessage="The Elite{br}Traveller"
        description="Travel personality heading with line break for elite traveller type"
        values={{ br: <br /> }}
      />
    case 'the-nature-roamer':
      return <FormattedMessage
        id="I4nYZwamAi"
        defaultMessage="The Nature{br}Roamer"
        description="Travel personality heading with line break for nature roamer type"
        values={{ br: <br /> }}
      />
    case 'the-romance-storyteller':
      return <FormattedMessage
        id="ugCWEqUGkf"
        defaultMessage="The romantic{br}retreat"
        description="Travel personality heading with line break for romance storyteller type"
        values={{ br: <br /> }}
      />
    case 'the-sun-chaser':
      return <FormattedMessage
        id="e410GVPc9r"
        defaultMessage="The Sun{br}Chaser"
        description="Travel personality heading with line break for sun chaser type"
        values={{ br: <br /> }}
      />
    case 'the-wellness-wanderer':
      return <FormattedMessage
        id="fws6QsZek1"
        defaultMessage="The Wellness{br}Wanderer"
        description="Travel personality heading with line break for wellness wanderer type"
        values={{ br: <br /> }}
      />
    case 'the-trend-explorer':
      return <FormattedMessage
        id="Area5nQOnn"
        defaultMessage="The Trend{br}Explorer"
        description="Travel personality heading with line break for trend explorer type"
        values={{ br: <br /> }}
      />
    case 'the-gourmet-hunter':
      return <FormattedMessage
        id="L4WjUQ8FtG"
        defaultMessage="The Gourmet{br}Hunter"
        description="Travel personality heading with line break for gourmet hunter type"
        values={{ br: <br /> }}
      />
  }
}

function TravelPersonalitySlide() {
  const intl = useIntl()
  const data = useWrappedStorySelector(state => state.data)

  if (!data) return null

  const { personality } = data
  const personalityName = getPersonalityName(intl, personality.type)
  const personalityDescription = getPersonalityDescription(intl, personality.type)
  const personalityImage = getImageUrl(PERSONALITY_IMAGE_IDS[personality.type], {
    format: 'png',
    width: 190,
  })

  const yearLabel = intl.formatMessage({
    id: '3++ZcI1SVK',
    defaultMessage: 'IN 2025',
    description: 'Travel personality slide year label in the year-end travel wrapped summary',
  })
  const travelStyleHeading = intl.formatMessage({
    id: '4Fj5KHVLp9',
    defaultMessage: 'Your travel style was',
    description: 'Travel personality slide heading in the year-end travel wrapped summary',
  })
  const percentileLabel = intl.formatMessage({
    id: 'S74sKbHipD',
    defaultMessage: '{percentile}% of travellers had a style like yours',
    description: 'Travel personality slide percentile label with variable',
  }, {
    percentile: PERSONALITY_PERCENTILES[personality.type],
  })

  return (
    <SlideBase verticalAlign="center">
      <Content>
        <PersonalityCard className={personality.type} paletteType="default">
          {/* <DecoLines /> */}
          <BodyText variant="large"><b>{yearLabel}</b></BodyText>
          <AvatarContainer>
            <PersonalityImage src={personalityImage} alt={personalityName} />
            <SparklesOverlay>
              <SparklesAnimation style={LottieStyle} />
            </SparklesOverlay>
          </AvatarContainer>
          <TitleSection>
            <Heading variant="heading5">{travelStyleHeading}</Heading>
            <Heading variant="heading1alt" align="center">
              <PersonalityDisplayName type={personality.type} />
            </Heading>
            <Label kind="primary" variant="dark">{percentileLabel}</Label>
          </TitleSection>
        </PersonalityCard>
        <BodyText variant="large" align="center">{personalityDescription}</BodyText>
      </Content>
    </SlideBase>
  )
}

export default React.memo(TravelPersonalitySlide)
